(ns quuppa.qsm.view
  "Global view elements, like top bar and sidebar."
  (:require [clojure.string :as str]
            [re-frame.core :as rf]
            ["react" :as react]
            ["react-dom" :as react-dom]
            [reagent.core :as r]
            [reitit.frontend.easy :as rfe]
            [quuppa.ui.util :as ui-util]
            [quuppa.ui.icons :refer [icon]]
            [quuppa.peweb.query :as q]
            [quuppa.qsm.events :as e]
            [quuppa.qsm.features :as features]
            [quuppa.qsm.signin.view :as signin]
            [quuppa.qsm.site.events]
            [quuppa.qsm.projects.events :as project-e]
            [quuppa.qsm.permissions :as permissions]
            [quuppa.ui.tooltip :as tooltip]
            [quuppa.ui.modal :as modal]
            [quuppa.qsm.release-notes.subs :as rn-subs]
            [quuppa.qsm.release-notes.events :as rn-events]
            ["remarkable" :refer [Remarkable]]
            ["remarkable/linkify" :refer [linkify]]
            ;; See swagger_markdown_plugin.cljs: dompurify resolves to its UMD
            ;; build whose `module.exports` is the instance (no `.default`).
            ["dompurify" :as DomPurify])
  (:require-macros [quuppa.common.version :as version]))

(def MainContext (react/createContext {}))

(defn nav-item [& args]
  (let [[props children] (ui-util/parse-args args)]
    (into [:li.nav-item.mb-1 props]
          children)))

(defn nav-link [{:keys [label href route-name active? class attrs]
                 :as props}]
  (let [current-page (or @(rf/subscribe [:nav/match :data :app-nav-page])
                         @(rf/subscribe [:nav/match :data :name]))
        parameters @(rf/subscribe [:nav/match :parameters :path])
        active?      (if active?
                       (active? current-page parameters)
                       (= route-name current-page))]
    [:a.nav-link.btn.btn-dark.d-flex.align-items-center.text-white
     (merge {:href  (or href
                        (rfe/href route-name))
             :class [class
                     (when active?
                       "active")]}
            (when active?
              {:aria-current "page"})
            attrs)
     (when (:icon props)
       [icon (:icon props)
        {:class "mr-3 fs-5"}])
     label
     (when (:icon-right props)
       [:<>
        [:div.flex-grow-1]
        [:button.btn.btn-sm.text-white.py-0.border-0
         {:type "button"
          :on-click (:icon-right-action props)}
         [icon (:icon-right props)
          {:class "fs-5"}]]])]))

(defn navbar []
  (let [{:keys [navbar-content-ref]} (react/useContext MainContext)]
    [:div.navbar.p-0.flex-shrink-0.justify-content-start

     ;; Match 1px border to navbar height
     [:button.navbar-toggler.d-md-none
      {:type "button"
       :data-toggle "collapse"
       :data-target :nav-menu
       :aria-controls "nav-menu"
       :aria-expanded "false"
       :aria-label "Toggle navigation"
       :on-click (fn [_e]
                   (rf/dispatch [::e/sidebar-toggle]))}
      [:span.navbar-toggler-icon]]

     [:div.qsm-sidebar.qsm-navbar-sidebar.h-100.d-flex.align-items-center
      [:a.navbar-brand
       {:data-test-id "navbar-logo"
        :href "#/"}
       [icon :quuppa-horizontal
        {:viewBox "0 0 88 18"}]]]

     [:nav.navbar.navbar-nav.flex-row.flex-grow-1.border-bottom
      ;; This component is only used if we already has a session
      ;; and the user has logged in.
      [:div.container-fluid.justify-content-between
       ;; Trick: have empty div displayed on small screen, so that
       ;; .justify-content-between will push the 'navbar-user-control's to
       ;; the right-most edge. Then hide it when viewport is sm or bigger and the navbar-content-ref kicks in.
       [:div.d-sm-none]
       [:div.d-none.d-sm-flex.align-items-center
        ;; Expose ref to this element, so portal can be used to
        ;; render to this div from other view components.
        ;; The navbar-content is hidden when viewport size is below 'sm' breakpoint.
        {:ref navbar-content-ref}]
       [signin/navbar-user-controls]]]]))

;; TODO: Problematic on smaller screens
(defn navbar-content
  "Render some content into the main top navbar through a React Portal"
  [& args]
  (let [[_props children] (ui-util/parse-args args)]
    ;; Could also use useContext hook, but this allows
    ;; using this component as a class component.
    [:> (.-Consumer MainContext)
     (fn [{:keys [navbar-content-ref]}]
       (when (.-current navbar-content-ref)
         (react-dom/createPortal
           (r/as-element (into [:<>] children))
           (.-current navbar-content-ref))))]))

(defn about-qsm-tooltip
  [content-fn auto-show? on-click]
  (let [show (r/atom (boolean auto-show?))]
    (fn [content-fn _auto-show? on-click]
      [tooltip/overlay-trigger
       {:placement "top"
        :rootClose true
        :trigger "click"
        :defaultShow (boolean auto-show?)
        :onToggle (fn [value]
                    (reset! show value))
        :show @show
        :overlay (r/as-element
                   [tooltip/tooltip
                    {:id "tooltip-about-qsm"}
                    (content-fn #(reset! show false))])}
       [:div.about-qsm-button.btn
        {:variant "link"
         :on-click on-click}
        "About QSM"]])))

(defn md->sanitized-html [markdown-str]
  (let [md        (new Remarkable #js {:html true :typographer true :breaks true :linkTarget "_blank"})
        _         (.use md linkify)
        _         (.disable (.. md -core -ruler) #js ["replacements", "smartquotes"])
        html      (.render md markdown-str)
        sanitized (.sanitize DomPurify html #js {:ADD_ATTR        #js ["target"]
                                                 :RETURN_TRUSTED_TYPE true
                                                 :FORBID_TAGS     #js ["style" "form"]
                                                 :ALLOW_DATA_ATTR false
                                                 :FORBID_ATTR     #js ["style" "class"]})]
    sanitized))

; Same as qsm-cc's quuppa.qcc.release_notes.api/note-version
(defn- note-version [version]
  (when version
    (let [parts (clojure.string/split version #"\.")]
      (if (> (count parts) 2)
        (clojure.string/join "." (take 2 parts))
        version))))

(defn- extract-preview-bullets
  "Pull the first `max-items` markdown bullet lines (lines starting with '- ' or '* ')
   out of a larger release-notes markdown document, ignoring headings, blockquotes/notes,
   and section titles. Used to build a short 'What's new' preview.
   Returns {:preview-md \"...\" :more-count N} or nil if no bullets found."
  [markdown-str max-items]
  (when markdown-str
    (let [bullet-lines (->> (str/split-lines markdown-str)
                            (map str/trim)
                            (filter #(re-find #"^[-*]\s+\S" %)))]
      (when (seq bullet-lines)
        {:preview-md  (str/join "\n" (take max-items bullet-lines))
         :more-count  (max 0 (- (count bullet-lines) max-items))}))))

(defn- extract-md-title
  "Extract a leading level-1 markdown heading ('# Title') from a release-notes markdown
   document, so it can be used as a modal title instead of being rendered twice
   (once in the header, once as an <h1> in the body).
   Returns {:title \"Title\" :body \"markdown with the heading line removed\"},
   or {:title nil :body markdown-str} if no leading H1 heading is found."
  [markdown-str]
  (if (str/blank? markdown-str)
    {:title nil :body markdown-str}
    (let [lines (str/split-lines markdown-str)
          [_blank-lines rest-lines] (split-with str/blank? lines)
          heading-line (some-> (first rest-lines) str/trim)
          [_ title] (when heading-line (re-matches #"#\s+(.+)" heading-line))]
      (if title
        {:title (str/trim title)
         :body (str/join "\n" (rest rest-lines))}
        {:title nil :body markdown-str}))))

(defn release-notes-modal [show-modal? release-notes-version release-notes-content loading?]
  (let [{:keys [title body]} (extract-md-title release-notes-content)]
    [modal/modal
     {:on-hide #(rf/dispatch [::rn-events/close-modal])
      :show show-modal?
      :size "md"}
     [:div.release-notes-modal-content
     [modal/header
      {:close-button true}
      [:div.release-notes-modal-title
       [:h2 "What's new"]
       [:span (or title (str "Quuppa Site Manager " (note-version release-notes-version)))]]]
     [modal/body
      {:style {:max-height "60vh" :overflow-y "auto"}
       :class "release-notes-modal-body"}
      (cond
        loading?
        [:p "Loading release notes..."]
        release-notes-content
        [:div.release-notes-content.border-top
         {:dangerouslySetInnerHTML {:__html (md->sanitized-html body)}}]
        :else
        [:p "No release notes available for this version."])]]]))

(defn qsm-about-overlay-content
  "Build the content for about QSM tooltip, showing release notes info if available"
  [{:keys [version-info user rn-available? release-notes-version release-notes-content loading? close-tooltip!]}]
  (let [app (:app version-info)
        app-version (:version app)
        app-branch-name (:branch-name app)
        build-id (:ref-short app)
        version-changed? (not= app-version (:last-seen-qsm-version user))
        {:keys [preview-md more-count]} (extract-preview-bullets release-notes-content 3)]
    [:div.qsm-about-overlay.text-left
     [:div.qsm-about-header
      (if version-changed?
        [:span.d-block (str "App updated to version: " app-version)]
        [:span.d-block (str "App version: " app-version)])
      (when build-id
        [:span.d-block.qsm-about-build (str "Build " build-id)])]
     (cond
       loading?
       [:div.release-notes-preview.border-top
        [:p.mb-0 "Loading release notes..."]]

       preview-md
       [:div.release-notes-preview.border-top
        [:div.release-notes-preview-title
         (str "✨ What's new in " (note-version release-notes-version))]
        [:div.release-notes-preview-content.release-notes-content
         {:dangerouslySetInnerHTML {:__html (md->sanitized-html preview-md)}}]
        (when (pos? more-count)
          [:p.release-notes-more.text-muted.small.mb-2 (str "+" more-count " more changes")])
        [:button.btn.btn-primary.btn-block
         {:on-click (fn []
                      (when close-tooltip!
                        (close-tooltip!))
                      (rf/dispatch [::rn-events/open-modal]))}
         "View release notes"]]

       rn-available?
       [:div.release-notes-preview.border-top
        [:p.mb-0 "Loading release notes..."]]

       (and (not version-changed?) (not= "master" app-branch-name))
       [:span.d-block.border-top.pt-2 (str "Branch name: " app-branch-name)]

       :else
       [:span.d-block.border-top.pt-2 "Release notes not available"])]))


(defn main-sidebar []
  (let [manage-privilege? (permissions/has-manage-permission?)
        [projects-expanded set-projects-expanded] (react/useState false)
        projects @(q/subscribe project-e/projects-query {} [:result])
        projects (->> projects (sort-by (comp str/lower-case :name)))
        is-qsm-owner? (permissions/is-qsm-owner?)
        version-info (version/get-version-info)
        app (:app version-info)
        app-version (:version app)
        user @(rf/subscribe [:user/user])
        show-modal @(rf/subscribe [::rn-subs/show-modal])
        release-notes-version @(rf/subscribe [::rn-subs/version])
        release-notes-content @(rf/subscribe [::rn-subs/content])
        release-notes-hash @(rf/subscribe [::rn-subs/hash])
        release-notes-loading? @(rf/subscribe [::rn-subs/loading?])
        release-notes-available? @(rf/subscribe [::rn-subs/available?])
        version-changed? (not= app-version (:last-seen-qsm-version user))
        rn-changed? (or version-changed?
                        (and (some? release-notes-hash)
                             (not= release-notes-hash (:last-seen-releasenote-hash user))))
        auto-show-tooltip? (and release-notes-available? rn-changed?)
        ;; Loads release notes content (for preview) if not already loaded, or
        ;; marks the version as seen when no release notes are available.
        load-release-notes! (fn []
                              (cond
                                (and release-notes-available? (nil? release-notes-content))
                                (rf/dispatch [::rn-events/fetch {:release-note-hash (:last-seen-releasenote-hash user)}])

                                (and (not release-notes-available?) version-changed?)
                                (rf/dispatch [::rn-events/mark-as-seen app-version nil])))
        overlay-content (fn [close-tooltip!]
                          (qsm-about-overlay-content
                            {:version-info version-info
                             :user user
                             :rn-available? release-notes-available?
                             :release-notes-version release-notes-version
                             :release-notes-content release-notes-content
                             :loading? release-notes-loading?
                             :close-tooltip! close-tooltip!}))]
    [:aside.qsm-sidebar.d-flex.flex-column
     {:class [(when @(rf/subscribe [::e/sidebar-open]) "show")]
      :style {:overflow-y "auto"}}

     [:nav#nav-menu.flex-grow-1.overflow-auto
      [:ul.nav.flex-column

       (when is-qsm-owner?
         [nav-item
          [nav-link
           {:label      "Dashboard"
            :icon       :briefcase
            :route-name :dashboard}]])

       [nav-item
        [nav-link
         {:label "Projects"
          :icon :briefcase
          :route-name :projects
          :active? (fn [current-page _parameters]
                     (or (= :projects current-page)
                         (and (not projects-expanded)
                              (= :project current-page))))
          :icon-right (if projects-expanded
                        :chevron-up
                        :chevron-down)
          :icon-right-action (fn [e]
                               (.preventDefault e)
                               (set-projects-expanded not))
          :attrs {:data-test-id "projects-link"}}]]

       (when projects-expanded
         (for [project projects]
           [nav-item
            {:key (:id project)}
            [nav-link
             {:class "pl-5 project-list-link"
              :label (:name project)
              :active? (fn [current-page parameters]
                         (and (= :project current-page)
                              (= (str (:id project)) (:project-id parameters))))
              :href (rfe/href :project {:project-id (:id project)})}]]))

       (when manage-privilege?
         [nav-item
          [nav-link
           {:label      "Engines"
            :icon       :tools
            :route-name :qpes
            :attrs      {:data-test-id "engines-link"}}]])

       (when manage-privilege?
         [nav-item
          [nav-link
           {:label "Users"
            :icon :people
            :route-name :users}]])
       (when (and manage-privilege?
                  (features/enabled? :planner))
         [nav-item
          [nav-link
           {:label "Planner"
            :icon :briefcase
            :route-name :planner}]])]]

     [about-qsm-tooltip
      overlay-content
      auto-show-tooltip?
      load-release-notes!]

     [release-notes-modal show-modal release-notes-version release-notes-content release-notes-loading?]]))

(defn root
  "The 'root' of the page, includes navbar and main-sidebar."
  [& args]
  (let [[props children] (ui-util/parse-args args)]
    [:div.d-flex.flex-column.flex-grow-1
     [:f> navbar props]
     (into [:div.d-flex.flex-grow-1.position-relative
            [:f> main-sidebar]]
           children)]))

;; For all views (as QPE views need this)
(def root-controller
  {:id            :qsm-root
   :start         (fn []
                    (rf/dispatch [::e/root-start]))
   :subscriptions (fn [_]
                    [;; Global notification:
                     [:qpe-boot/event-report]])})
